import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { FeedChannel } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

export interface RotateFeedDialogProps {
  /** Null keeps the dialog closed. */
  channel: FeedChannel | null;
  pending: boolean;
  error: unknown;
  onConfirm: (channel: FeedChannel) => void;
  onOpenChange: (open: boolean) => void;
}

/** Rotating replaces the URL the channel fetches from, so it asks first. */
export function RotateFeedDialog({
  channel,
  pending,
  error,
  onConfirm,
  onOpenChange,
}: RotateFeedDialogProps) {
  const { t } = useTranslation(['feeds', 'common']);
  const message = useErrorMessage();
  const name = channel ? t(`feeds:channels.${channel}.name`) : '';

  return (
    <AlertDialog open={channel !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('feeds:rotate.title', { channel: name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('feeds:rotate.description', { channel: name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (channel) onConfirm(channel);
            }}
          >
            {pending ? t('feeds:rotate.submitting') : t('feeds:rotate.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
